import { useCachedFetch } from '../../shared/hooks';
import apiRoutes from '../../../config/api/routes';
import { useParams } from 'react-router';
import { UrlParams } from '../../shared/types';
import { useUser } from '../../shared';
import { ITeacherFeedback } from '../../../config/api/models/Feedback';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

export const useTeacherFeedback = () => {
  const { isTeacher } = useUser();
  const { bookId, chapterId, trajectoryId } = useParams<UrlParams>();
  const { selectedGroup, selectedStudent } = useAppSelector((state) => state.filters);

  const teacherFeedbackByGroup = useCachedFetch<{ data: ITeacherFeedback[] }>(
    ['teacherFeedbackByGroup', selectedGroup],
    apiRoutes.getTeacherFeedbackByGroup(
      bookId as string,
      chapterId as string,
      trajectoryId as string,
      selectedGroup || ''
    ),
    {
      method: 'GET',
      options: {
        enabled: selectedGroup !== '' && isTeacher,
        cacheTime: 1000,
      },
    }
  );

  const teacherFeedbackByStudent = useCachedFetch<{ data: ITeacherFeedback[] }>(
    ['teacherFeedbackByStudent', selectedStudent],
    apiRoutes.getTeacherFeedbackByStudent(
      bookId as string,
      chapterId as string,
      trajectoryId as string,
      selectedGroup || '',
      selectedStudent || ''
    ),
    {
      method: 'GET',
      options: {
        enabled: selectedStudent !== '' && isTeacher,
        cacheTime: 1000,
      },
    }
  );

  const result: ITeacherFeedback[] | undefined = useMemo(
    () =>
      selectedStudent === ''
        ? teacherFeedbackByGroup?.data?.data
        : teacherFeedbackByStudent?.data?.data,
    [selectedStudent, teacherFeedbackByGroup, teacherFeedbackByStudent]
  );

  const refetch = useCallback(() => {
    if (isTeacher) {
      if (selectedStudent === '') {
        teacherFeedbackByGroup.refetch().then();
      } else {
        teacherFeedbackByStudent.refetch().then();
      }
    }
  }, [isTeacher, teacherFeedbackByGroup, teacherFeedbackByStudent, selectedStudent]);

  return {
    feedback: result,
    refetch,
  };
};
